//@import dependencies
import React, { CSSProperties, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
//@end

//@import components
import AppContainer from 'app_components/layouts/app'
import BreadcrumbComponent from 'app_components/commons/breadcrumb';
//@end

// @import utils
import { ScreenNotificationsUtil } from 'app_utils/screenNotification'
// @end

// @import hooks
import { useParams } from 'react-router-dom'
// @end

// @INFO Import types
// @end

// @INFO Import services
import { routingService } from 'app_services/routingService';
import SurveyService from 'app_services/admin/academicContent/survey/surveyService';
// @end

// @INFO Import Styles
import './../editorlistScreen.scss'
import ButtonComponent from 'app_components/commons/Button/ButtonComponent';
import InputComponent from 'app_components/commons/Input/InputComponent';
import { BiSearchAlt2 } from 'react-icons/bi';
import { MdAddCircleOutline } from 'react-icons/md';
import DataTableComponent from 'app_components/commons/dataTable';
import EditorService from 'app_services/editor/editorService';
import storeService from 'app_services/storeService';
import { HiOutlineDocumentReport } from 'react-icons/hi';
import ModalSatisfactionReport from 'app_components/modules/ModalSatisfactionReport/ModalSatisfactionReport';

interface ISurveyList{
  _id: string
  status: string
  name: string
  config:{
    content:{
      _id: string
      academic_resource: {
        _id: string
        title: string
        description: string
      }
    }
  }
}

// @ADD Component types
interface IEditorlistScreenLayoutProps {
  history?: any
  permission: any
}

const courseTypeNames = {
  free: 'Gratuito',
  mooc: 'Mooc'
}

const EditorlistScreenLayout: React.FC<IEditorlistScreenLayoutProps> = (props) => {

  const [surveys, updateSurveys] = useState<ISurveyList[]>([])
  const [totalSurveys, updateTotalSurveys] = useState<ISurveyList[]>([])
  const [search, updateSearch] = useState<string>('')
  const [loadingCreate, updateLoadingCreate] = useState<boolean>(false)
  const [openReport, updateOpenReport] = useState<boolean>(false);
  const customModalities = {
    'characterization-survey': 'Encuesta de caracterización',
    'withoutTutor-survey': 'Cursos autogestionados',
  }

  const dataTableSurveys = useMemo(() => surveys?.map((survey) => {
    // @ts-ignore
    const courseModeName = customModalities[survey?.config?.content?.config?.course_modes] ?
      // @ts-ignore
      customModalities[survey?.config?.content?.config?.course_modes] : survey?.config?.content?.config?.course_modes?.name
    // @ts-ignore
    const typeCourse = courseModeName === 'Virtual' && courseTypeNames[survey?.config?.content?.config?.course_type] ? courseTypeNames[survey?.config?.content?.config?.course_type] : ''
    return {
      ...survey,
      courseModeName: `${courseModeName} ${typeCourse}`
    }
  }), [surveys])

  const { t } = useTranslation()

  const params = useParams<{idCategory: string}>()

  // @INFO Servicios
  const editorService = new EditorService()
  const surveyService = new SurveyService()
  const screenNotifications = new ScreenNotificationsUtil()

  useEffect(() => {
    getSurveys()
  },[])

  useEffect(() => {
    filterSurveys()
  },[search, totalSurveys])

  /**
   * @INFO Obtener la lista de encuestas
   */
  const getSurveys = async () => {
    const response = await editorService.getSurveyList()
    if(response?.code === 200 && response?.surveys?.length){
      updateTotalSurveys(response.surveys)
    }else{
      updateTotalSurveys([])
    }
  }

  /**
   * @INFO Click en editar una encuesta
   * @param _survey
   * @returns
   */
  const handleClickEditSurvey = (_survey: ISurveyList) => {
    if(!_survey) return
    const route = routingService.getSecurePath(`/create_survey?survey=${_survey._id}&config=${_survey.config.content._id}`)
    if(props.history){
      props.history.push(route)
    }
  }

  /**
   * @INFO Crear una nueva encuesta y redirigir a la pantalla de catalogación
   */
  const handleClickNewSurvey = async () => {
    const category = storeService?.getAcademicResourceCategoryFromName('survey')
    if(!category) return
    updateLoadingCreate(true)
    const response = await editorService.createGhostResource({
      academic_resource_category: category
    })
    if(response?.code === 200 && response?.resource_data){
      getSurveys()
      // @INFO Redirigir a la pantalla de catalogación
      const data = response.resource_data
      const route = routingService.getSecurePath(`/create_survey?survey=${data.survey}&config=${data.academicResourceConfig}`)
      if(props.history){
        props.history.push(route)
      }
    }
    updateLoadingCreate(false)
  }

  const handleClickDeleteSurvey = async (id: string) => {
    let confirm = screenNotifications.fire({
      message: `${t('screens.survey.delete.message')}`,
    },
    {
      type: 'warning',
      title: `${t('screens.survey.delete.title')}`,
      confirmButtonText: `${t('components.screen_notifications.buttons.confirmButtonText')}`,
      cancelButtonText: `${t('components.screen_notifications.buttons.cancelButtonText')}`,
      showCancelButton: true,
    })

    if (confirm) {
      confirm.then(async (result) => {
        if (result.value) {
          // this.setState({ status: 'loading' })

          const remove = await surveyService.remove(id)

          if (remove) {
            screenNotifications.fireToast(
              {
                message: `${t('components.screen_notifications.delete')}`,
              },
              {
                position: 'bottom-right',
              },
            )

            getSurveys()
          }

          // this.setState({ status: null })
        }
      })
    }
  }

  /**
   * @INFO Filtrar encuestas
   */
  const filterSurveys = () => {
    if(search.trim().length){
      const newSurveys = totalSurveys.reduce((accum: ISurveyList[], item) => {
        if(item.name.toLowerCase().includes(search.toLowerCase())){
          accum.push({...item})
        }
        return accum
      },[])
      updateSurveys([...newSurveys])
    }else{
      updateSurveys([...totalSurveys])
    }
  }

  return(
    <AppContainer>
      <div className='container-fluid editor_list_screen_layout-layout'>
        <BreadcrumbComponent links={['#']} texts={[t('components.breadcrumb.survey')]} />
        <h2>Encuestas</h2>
        <div className="container-actions">
          <InputComponent
            placeholder='Buscar...'
            onChange={(e) => updateSearch(e.target.value)}
            value={search}
          >
            <InputComponent.Icon>
              <BiSearchAlt2/>
            </InputComponent.Icon>
          </InputComponent>
          {props.permission.checkSimple('permission:survey_create') && (
            <ButtonComponent
              variant='secondary'
              onClick={() => handleClickNewSurvey()}
              loading={loadingCreate}
            >
              <ButtonComponent.Icon>
                <MdAddCircleOutline/>
              </ButtonComponent.Icon>
              Nuevo
            </ButtonComponent>
          )}
          {props.permission.checkSimple('permission:survey_report') && (
            <ButtonComponent
              variant='secondary'
              onClick={() => updateOpenReport(true)}
            >
              <ButtonComponent.Icon>
                <HiOutlineDocumentReport/>
              </ButtonComponent.Icon>
              Reporte
            </ButtonComponent>
          )}
        </div>
        <div className="editor-list-items">
          <DataTableComponent
            data={dataTableSurveys}
            keys={[
              'name',
              'config.content.academic_resource.description',
              'courseModeName',
              'statusLabel'
            ]}
            headerKeys={[
              'Nombre',
              'Descripción',
              'Modalidad',
              'Estado'
            ]}
            onEditRow={props.permission.checkSimple('permission:survey_update') ? (_edit, _row) => handleClickEditSurvey(_row) : undefined}
            onRemoveRow={props.permission.checkSimple('permission:survey_delete') ? (id) => handleClickDeleteSurvey(id) : undefined}
          />
        </div>
        <ModalSatisfactionReport
          open={openReport}
          onClose={() => updateOpenReport(false)}
        />
      </div>
    </AppContainer>
  )
}



export default EditorlistScreenLayout
